import * as i0 from '@angular/core';
import { EventEmitter, Component, ViewEncapsulation, ChangeDetectionStrategy, ViewChild, Directive, Input, Output, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, Subject, merge, fromEvent, map, takeUntil, distinctUntilChanged, skip, filter, timer } from 'rxjs';
import { createPopper } from '@popperjs/core';
const _c0 = ["popperViewRef"];
function PopperContentComponent_div_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 5);
    i0.ɵɵprojection(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("innerHTML", ctx_r1.text, i0.ɵɵsanitizeHtml);
  }
}
function PopperContentComponent_div_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 6);
    i0.ɵɵprojection(1, 1);
    i0.ɵɵelementEnd();
  }
}
const _c1 = ["*", "*"];
var PopperPlacements = /*#__PURE__*/function (PopperPlacements) {
  PopperPlacements["TOP"] = "top";
  PopperPlacements["BOTTOM"] = "bottom";
  PopperPlacements["LEFT"] = "left";
  PopperPlacements["RIGHT"] = "right";
  PopperPlacements["TOPSTART"] = "top-start";
  PopperPlacements["BOTTOMSTART"] = "bottom-start";
  PopperPlacements["LEFTSTART"] = "left-start";
  PopperPlacements["RIGHTSTART"] = "right-start";
  PopperPlacements["TOPEND"] = "top-end";
  PopperPlacements["BOTTOMEND"] = "bottom-end";
  PopperPlacements["LEFTEND"] = "left-end";
  PopperPlacements["RIGHTEND"] = "right-end";
  PopperPlacements["AUTO"] = "auto";
  PopperPlacements["AUTOSTART"] = "auto-start";
  PopperPlacements["AUTOEND"] = "auto-end";
  return PopperPlacements;
}(PopperPlacements || {});
var PopperTriggers = /*#__PURE__*/function (PopperTriggers) {
  PopperTriggers["click"] = "click";
  PopperTriggers["hover"] = "hover";
  PopperTriggers["mousedown"] = "mousedown";
  PopperTriggers["none"] = "none";
  return PopperTriggers;
}(PopperTriggers || {});
let PopperContentComponent = /*#__PURE__*/(() => {
  class PopperContentComponent {
    static {
      this.nextId = 0;
    }
    constructor(elementRef, _renderer, _viewRef, _changeDetectorRef, ngZone) {
      this.elementRef = elementRef;
      this._renderer = _renderer;
      this._viewRef = _viewRef;
      this._changeDetectorRef = _changeDetectorRef;
      this.ngZone = ngZone;
      this.ariaHidden = 'true';
      this.arrowColor = null;
      this.displayType = 'none';
      this.id = `poppper_${++PopperContentComponent.nextId}`;
      this.onHidden = new EventEmitter();
      this.opacity = 0;
      this.options = {
        disableAnimation: false,
        disableDefaultStyling: false,
        placement: PopperPlacements.AUTO,
        boundariesElement: '',
        trigger: PopperTriggers.hover,
        positionFixed: false,
        popperModifiers: []
      };
      this.state = true;
      this.isMouseOver$ = new BehaviorSubject(false);
      this._styleId = `${this.id}_style`;
      this.destroy$ = new Subject();
    }
    ngOnInit() {
      const element = this.elementRef.nativeElement;
      merge(fromEvent(element, 'mouseover'), fromEvent(element, 'mouseleave')).pipe(map(evt => evt.type === 'mouseover'), takeUntil(this.destroy$), distinctUntilChanged()).subscribe(this.isMouseOver$);
    }
    clean() {
      this.toggleVisibility(false);
      if (!this.popperInstance) {
        return;
      }
      this.popperInstance.destroy();
    }
    extractAppliedClassListExpr(classList = []) {
      const klassList = Array.isArray(classList) ? classList : typeof classList === typeof '' ? classList.replace(/ /, '').split(',') : [];
      return klassList.reduce((acc, klass) => {
        acc[klass] = true;
        return acc;
      }, {});
    }
    hide() {
      if (this.popperInstance) {
        this.popperInstance.destroy();
      }
      this.toggleVisibility(false);
      this.onHidden.emit();
    }
    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
      this.clean();
      if (this.options.appendTo && this.elementRef && this.elementRef.nativeElement && this.elementRef.nativeElement.parentNode) {
        this._viewRef.detach();
        this.elementRef.nativeElement.parentNode.removeChild(this.elementRef.nativeElement);
      }
    }
    onDocumentResize() {
      this.update();
    }
    show() {
      const referenceObject = this.referenceObject;
      if (!referenceObject) {
        return;
      }
      const appendToParent = this.options.appendTo && document.querySelector(this.options.appendTo);
      if (appendToParent && this.elementRef.nativeElement.parentNode !== appendToParent) {
        this.elementRef.nativeElement.parentNode && this.elementRef.nativeElement.parentNode.removeChild(this.elementRef.nativeElement);
        appendToParent.appendChild(this.elementRef.nativeElement);
      }
      const popperOptions = {
        strategy: this.options.positionFixed ? 'fixed' : 'absolute',
        placement: this.options.placement,
        modifiers: [{
          name: 'offset',
          enabled: true,
          options: {
            offset: [0, 8]
          }
        }, {
          name: 'arrow',
          enabled: true,
          options: {
            element: '.popper-arrow',
            padding: 3
          },
          requires: ['arrow']
        }]
      };
      popperOptions.onFirstUpdate = this.onUpdate;
      const boundariesElement = this.options.boundariesElement && document.querySelector(this.options.boundariesElement);
      if (popperOptions.modifiers && boundariesElement) {
        popperOptions.modifiers.push({
          name: 'preventOverflow',
          enabled: this.options.preventOverflow,
          options: {
            boundary: boundariesElement
          }
        });
      }
      if (popperOptions.modifiers) {
        const preventOverflowModifier = popperOptions.modifiers.find(v => v.name === 'preventOverflow');
        if (preventOverflowModifier && !preventOverflowModifier.enabled) {
          const hideModifier = popperOptions.modifiers.find(v => v.name === 'preventOverflow');
          hideModifier && (hideModifier.enabled = false);
        }
      }
      const modifiers = this.options?.popperModifiers;
      if (modifiers) {
        popperOptions.modifiers = popperOptions.modifiers.concat(modifiers);
      }
      const viewRef = this.popperViewRef;
      if (!viewRef) {
        throw new Error('Popper view ref not found');
      }
      this.ngZone.runOutsideAngular(() => {
        this.popperInstance = createPopper(referenceObject, viewRef.nativeElement, popperOptions);
      });
      this.toggleVisibility(true);
      this._globalResize = this._renderer.listen('document', 'resize', this.onDocumentResize.bind(this));
    }
    toggleVisibility(state) {
      if (!state) {
        this.opacity = 0;
        this.displayType = 'none';
        this.ariaHidden = 'true';
        this.state = false;
      } else {
        this.opacity = 1;
        this.displayType = 'block';
        this.ariaHidden = 'false';
        this.state = true;
      }
      this._changeDetectorRef.detectChanges();
    }
    update() {
      this.popperInstance?.update();
    }
    static {
      this.ɵfac = function PopperContentComponent_Factory(t) {
        return new (t || PopperContentComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.NgZone));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: PopperContentComponent,
        selectors: [["met-popper-content"]],
        viewQuery: function PopperContentComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(_c0, 7);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.popperViewRef = _t.first);
          }
        },
        exportAs: ["popperContent"],
        ngContentSelectors: _c1,
        decls: 5,
        vars: 19,
        consts: [[3, "ngStyle", "ngClass"], ["popperViewRef", ""], ["class", "popper-inner", 3, "innerHTML", 4, "ngIf"], ["class", "popper-inner", 4, "ngIf"], ["data-popper-arrow", "", 1, "popper-arrow", 3, "ngClass"], [1, "popper-inner", 3, "innerHTML"], [1, "popper-inner"]],
        template: function PopperContentComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef(_c1);
            i0.ɵɵelementStart(0, "div", 0, 1);
            i0.ɵɵtemplate(2, PopperContentComponent_div_2_Template, 2, 1, "div", 2)(3, PopperContentComponent_div_3_Template, 2, 0, "div", 3);
            i0.ɵɵelement(4, "div", 4);
            i0.ɵɵelementEnd();
          }
          if (rf & 2) {
            let tmp_4_0;
            i0.ɵɵstyleProp("display", ctx.displayType)("opacity", ctx.opacity);
            i0.ɵɵclassProp("popper-container", !ctx.options.disableDefaultStyling)("popper-animation", !ctx.options.disableAnimation);
            i0.ɵɵproperty("ngStyle", (tmp_4_0 = ctx.options.styles) !== null && tmp_4_0 !== undefined ? tmp_4_0 : null)("ngClass", ctx.extractAppliedClassListExpr(ctx.options.applyClass));
            i0.ɵɵattribute("id", ctx.id)("aria-hidden", ctx.ariaHidden)("aria-describedby", ctx.options.ariaDescribe || null)("role", ctx.options.ariaRole);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("ngIf", ctx.text);
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngIf", !ctx.text);
            i0.ɵɵadvance();
            i0.ɵɵclassProp("popper-force-arrow", ctx.arrowColor);
            i0.ɵɵproperty("ngClass", ctx.extractAppliedClassListExpr(ctx.options.applyArrowClass));
          }
        },
        dependencies: [i1.NgClass, i1.NgIf, i1.NgStyle],
        styles: ["met-popper-content{position:relative;display:block}.popper-container{display:none;position:absolute;border-radius:3px;border:1px solid grey;box-shadow:0 0 2px #00000080;padding:10px}.popper-container.popper-animation{animation:popper-fadeIn .15s ease-out}.popper-container>.popper-arrow,.popper-container>.popper-arrow:before{position:absolute;width:10px;height:10px;z-index:-1}.popper-container>.popper-arrow:before{transform:rotate(45deg);content:\"\";background-color:#fff;top:0;left:0}.popper-container[data-popper-placement^=top]>.popper-arrow{bottom:-4px}.popper-container[data-popper-placement^=bottom]>.popper-arrow{top:-4px}.popper-container[data-popper-placement^=left]>.popper-arrow{right:-4px}.popper-container[data-popper-placement^=right]>.popper-arrow{left:-4px}@keyframes popper-fadeIn{0%{display:none;opacity:0}1%{display:block;opacity:0}to{display:block;opacity:1}}\n"],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return PopperContentComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @private
 */
class PopperUtils {
  /** Coerces a data-bound value (typically a string) to a boolean. */
  static coerceBooleanProperty(value) {
    return value != null && `${value}` !== 'false';
  }
}

/* eslint-disable @angular-eslint/no-input-rename */
class PopperDirective {
  static {
    this.baseOptions = {
      showDelay: 0,
      placement: PopperPlacements.AUTO,
      hideOnClickOutside: true,
      hideOnMouseLeave: false,
      hideOnScroll: false,
      appendTo: undefined,
      ariaRole: 'popper',
      ariaDescribe: '',
      styles: {},
      trigger: PopperTriggers.click
    };
  }
  static {
    this.nextId = 0;
  }
  set applyClass(newValue) {
    if (newValue === this._applyClass) {
      return;
    }
    this._applyClass = newValue;
    this._checkExisting('applyClass', newValue);
  }
  get applyClass() {
    return this._applyClass;
  }
  set content(newValue) {
    if (newValue === this._content) {
      return;
    }
    this._content = newValue;
    if (this._popperContent) {
      if (typeof newValue === 'string') {
        this._popperContent.text = newValue;
      } else {
        this._popperContent = newValue;
      }
    }
  }
  get content() {
    return this._content;
  }
  set disabled(newValue) {
    if (newValue === this._disabled) {
      return;
    }
    this._disabled = !!newValue;
    if (this._shown) {
      this.hide();
    }
  }
  get disabled() {
    return this._disabled;
  }
  set placement(newValue) {
    this._popperPlacement = newValue;
    this._checkExisting('placement', newValue);
  }
  get placement() {
    return this._popperPlacement;
  }
  set popperApplyArrowClass(newValue) {
    if (newValue === this._popperApplyArrowClass) {
      return;
    }
    this._popperApplyArrowClass = newValue;
    if (this._popperContent) {
      this._popperContent.options.applyArrowClass = newValue;
      if (!this._shown) {
        return;
      }
      this._popperContent.popperInstance?.setOptions(this._popperContent.options);
    }
  }
  get popperApplyArrowClass() {
    return this._popperApplyArrowClass;
  }
  set preventOverflow(newValue) {
    this._popperPreventOverflow = PopperUtils.coerceBooleanProperty(newValue);
    this._checkExisting('preventOverflow', this._popperPreventOverflow);
  }
  get preventOverflow() {
    return this._popperPreventOverflow;
  }
  constructor(_changeDetectorRef, viewContainerRef, _elementRef) {
    this._changeDetectorRef = _changeDetectorRef;
    this.viewContainerRef = viewContainerRef;
    this._elementRef = _elementRef;
    this.hideTimeout = 0;
    this.popperOnHidden = new EventEmitter();
    this.popperOnShown = new EventEmitter();
    this.popperOnUpdate = new EventEmitter();
    this.timeoutAfterShow = 0;
    this._destroy$ = new Subject();
    this._globalEventListenersCtrl$ = new Subject();
    this._popperContentClass = PopperContentComponent;
    this._scheduledHideTimeoutCtrl$ = new Subject();
    this._scheduledShowTimeoutCtrl$ = new Subject();
    this._shown = false;
  }
  static assignDefined(target, ...sources) {
    for (const source of sources) {
      for (const key of Object.keys(source)) {
        const val = source[key];
        if (val !== undefined) {
          target[key] = val;
        }
      }
    }
    return target;
  }
  applyTriggerListeners() {
    switch (this.showTrigger) {
      case PopperTriggers.click:
        this._addListener('click', this.toggle.bind(this));
        break;
      case PopperTriggers.mousedown:
        this._addListener('mousedown', this.toggle.bind(this));
        break;
      case PopperTriggers.hover:
        this._addListener('mouseenter', this.scheduledShow.bind(this, this.showDelay));
        ['touchend', 'touchcancel', 'mouseleave'].forEach(eventName => {
          this._addListener(eventName, () => this.scheduledHide());
        });
        this._popperContent?.isMouseOver$.pipe(takeUntil(this._destroy$), skip(1), filter(v => !v)).subscribe(() => this.scheduledHide(this.hideTimeout));
        break;
    }
    if (this.showTrigger !== PopperTriggers.hover && this.hideOnMouseLeave) {
      ['touchend', 'touchcancel', 'mouseleave'].forEach(eventName => {
        this._addListener(eventName, () => this.scheduledHide());
      });
    }
  }
  getRefElement() {
    return this._elementRef.nativeElement;
  }
  hide() {
    if (this.disabled) {
      return;
    }
    if (!this._shown) {
      this._scheduledShowTimeoutCtrl$.next();
      return;
    }
    this._shown = false;
    if (this._popperContentRef) {
      this._popperContentRef.instance.hide();
    } else {
      this._popperContent?.hide();
    }
    this.popperOnHidden.emit(this);
    this._globalEventListenersCtrl$.next();
  }
  hideOnClickOutsideHandler($event) {
    if (this.disabled || !this.hideOnClickOutside || $event.target === this._popperContent?.elementRef.nativeElement || this._popperContent?.elementRef.nativeElement.contains($event.target)) {
      return;
    }
    this.scheduledHide(this.hideTimeout);
  }
  hideOnScrollHandler() {
    if (this.disabled || !this.hideOnScroll) {
      return;
    }
    this.scheduledHide(this.hideTimeout);
  }
  ngOnDestroy() {
    this._destroy$.next();
    this._popperContentRef?.destroy();
    this._popperContent?.clean();
  }
  ngOnInit() {
    // Support legacy prop
    this.hideOnClickOutside = typeof this.hideOnClickOutside === 'undefined' ? this.closeOnClickOutside : this.hideOnClickOutside;
    if (typeof this.content === 'string') {
      this._popperContentRef = this._constructContent();
      this._popperContent = this._popperContentRef.instance;
      this._popperContent.text = this.content;
    } else if (typeof this.content === typeof void 0) {
      this._popperContentRef = this._constructContent();
      this._popperContent = this._popperContentRef.instance;
      this._popperContent.text = '';
    } else {
      this._popperContent = this.content;
    }
    const popperRef = this._popperContent;
    if (popperRef) {
      popperRef.referenceObject = this.getRefElement();
      this._setContentProperties(popperRef);
    }
    this._setDefaults();
    this.applyTriggerListeners();
    if (this.showOnStart) {
      this.scheduledShow();
    }
  }
  scheduledHide(delay = this.hideTimeout) {
    if (this.disabled) {
      return;
    }
    this._scheduledShowTimeoutCtrl$.next();
    timer(delay).pipe(takeUntil(this._scheduledHideTimeoutCtrl$), takeUntil(this._destroy$)).subscribe({
      next: () => {
        const viewElement = this._popperContent?.popperViewRef?.nativeElement;
        if (!viewElement || this.content instanceof PopperContentComponent && this.content.isMouseOver$.value) {
          return;
        }
        this.hide();
        this._applyChanges();
      }
    });
  }
  scheduledShow(delay = this.showDelay ?? 0) {
    if (this.disabled) {
      return;
    }
    this._scheduledHideTimeoutCtrl$.next();
    timer(delay).pipe(takeUntil(this._scheduledShowTimeoutCtrl$), takeUntil(this._destroy$)).subscribe({
      next: () => {
        this.show();
        this._applyChanges();
      }
    });
  }
  show() {
    if (this._shown) {
      this._scheduledHideTimeoutCtrl$.next();
      return;
    }
    this._shown = true;
    const popperRef = this._popperContent;
    if (!popperRef) {
      throw new Error('Popper content not set');
    }
    const element = this.getRefElement();
    if (popperRef.referenceObject !== element) {
      popperRef.referenceObject = element;
    }
    this._setContentProperties(popperRef);
    popperRef.show();
    this.popperOnShown.emit(this);
    if (this.timeoutAfterShow > 0) {
      this.scheduledHide(this.timeoutAfterShow);
    }
    fromEvent(document, 'click').pipe(takeUntil(this._globalEventListenersCtrl$), takeUntil(this._destroy$)).subscribe(e => this.hideOnClickOutsideHandler(e));
    const scrollParent = this._getScrollParent(this.getRefElement());
    if (!scrollParent) {
      return;
    }
    fromEvent(scrollParent, 'scroll', {
      passive: true
    }).pipe(takeUntil(this._globalEventListenersCtrl$), takeUntil(this._destroy$)).subscribe(() => this.hideOnScrollHandler());
  }
  toggle() {
    if (this.disabled) {
      return;
    }
    this._shown ? this.scheduledHide(this.hideTimeout) : this.scheduledShow();
  }
  _addListener(eventName, cb) {
    fromEvent(this._elementRef.nativeElement, eventName).pipe(takeUntil(this._destroy$)).subscribe(cb);
  }
  _applyChanges() {
    this._changeDetectorRef.markForCheck();
    this._changeDetectorRef.detectChanges();
  }
  _checkExisting(key, newValue) {
    const options = this._popperContent?.options;
    const instance = this._popperContent?.popperInstance;
    if (!options || !instance) {
      return;
    }
    options[key] = newValue;
    if (!this._shown) {
      return;
    }
    instance.setOptions(options);
  }
  _constructContent() {
    return this.viewContainerRef.createComponent(this._popperContentClass);
  }
  _getScrollParent(node) {
    if (node === document.body) {
      return document;
    }
    const overflowY = window.getComputedStyle(node).overflowY;
    const isScrollable = overflowY !== 'visible' && overflowY !== 'hidden';
    if (isScrollable && node.scrollHeight >= node.clientHeight) {
      return node;
    }
    return node.parentElement && this._getScrollParent(node.parentElement) || document;
  }
  _onPopperUpdate(event) {
    this.popperOnUpdate.emit(event);
  }
  _setContentProperties(popperRef) {
    popperRef.options = PopperDirective.assignDefined(popperRef.options, PopperDirective.baseOptions, {
      showDelay: this.showDelay,
      disableAnimation: this.disableAnimation,
      disableDefaultStyling: this.disableStyle,
      placement: this.placement,
      boundariesElement: this.boundariesElement,
      trigger: this.showTrigger,
      positionFixed: this.positionFixed,
      popperModifiers: this.popperModifiers,
      ariaDescribe: this.ariaDescribe,
      ariaRole: this.ariaRole,
      applyClass: this.applyClass,
      applyArrowClass: this.popperApplyArrowClass,
      hideOnMouseLeave: this.hideOnMouseLeave,
      styles: this.styles,
      appendTo: this.popperAppendTo,
      preventOverflow: this.preventOverflow
    });
    popperRef.onUpdate = this._onPopperUpdate.bind(this);
    popperRef.onHidden.pipe(takeUntil(this._destroy$)).subscribe(this.hide.bind(this));
  }
  _setDefaults() {
    const keys = ['showDelay', 'hideOnScroll', 'hideOnMouseLeave', 'hideOnClickOutside', 'ariaRole', 'ariaDescribe'];
    keys.forEach(key => {
      const _this = this;
      _this[key] = _this[key] ?? PopperDirective.baseOptions[key];
    });
    this.showTrigger = this.showTrigger || PopperDirective.baseOptions.trigger;
    this.styles = this.styles === void 0 ? {
      ...PopperDirective.baseOptions.styles
    } : this.styles;
  }
  static {
    this.ɵfac = function PopperDirective_Factory(t) {
      return new (t || PopperDirective)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(i0.ElementRef));
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: PopperDirective,
      selectors: [["", "metPopper", ""]],
      inputs: {
        applyClass: [i0.ɵɵInputFlags.None, "metPopperApplyClass", "applyClass"],
        ariaDescribe: [i0.ɵɵInputFlags.None, "metPopperAriaDescribeBy", "ariaDescribe"],
        ariaRole: [i0.ɵɵInputFlags.None, "metPopperAriaRole", "ariaRole"],
        boundariesElement: [i0.ɵɵInputFlags.None, "metPopperBoundaries", "boundariesElement"],
        closeOnClickOutside: [i0.ɵɵInputFlags.None, "metPopperCloseOnClickOutside", "closeOnClickOutside"],
        content: [i0.ɵɵInputFlags.None, "metPopper", "content"],
        disableAnimation: [i0.ɵɵInputFlags.None, "metPopperDisableAnimation", "disableAnimation"],
        disabled: [i0.ɵɵInputFlags.None, "metPopperDisabled", "disabled"],
        disableStyle: [i0.ɵɵInputFlags.None, "metPopperDisableStyle", "disableStyle"],
        hideOnClickOutside: [i0.ɵɵInputFlags.None, "metPopperHideOnClickOutside", "hideOnClickOutside"],
        hideOnMouseLeave: [i0.ɵɵInputFlags.None, "metPopperHideOnMouseLeave", "hideOnMouseLeave"],
        hideOnScroll: [i0.ɵɵInputFlags.None, "metPopperHideOnScroll", "hideOnScroll"],
        hideTimeout: [i0.ɵɵInputFlags.None, "metPopperTimeout", "hideTimeout"],
        placement: [i0.ɵɵInputFlags.None, "metPopperPlacement", "placement"],
        popperAppendTo: "popperAppendTo",
        popperApplyArrowClass: "popperApplyArrowClass",
        popperModifiers: "popperModifiers",
        positionFixed: [i0.ɵɵInputFlags.None, "metPopperPositionFixed", "positionFixed"],
        preventOverflow: [i0.ɵɵInputFlags.None, "metPopperPreventOverflow", "preventOverflow"],
        showDelay: [i0.ɵɵInputFlags.None, "metPopperDelay", "showDelay"],
        showOnStart: [i0.ɵɵInputFlags.None, "metPopperShowOnStart", "showOnStart"],
        showTrigger: [i0.ɵɵInputFlags.None, "metPopperTrigger", "showTrigger"],
        styles: [i0.ɵɵInputFlags.None, "metPopperStyles", "styles"],
        timeoutAfterShow: [i0.ɵɵInputFlags.None, "metPopperTimeoutAfterShow", "timeoutAfterShow"]
      },
      outputs: {
        popperOnHidden: "popperOnHidden",
        popperOnShown: "popperOnShown",
        popperOnUpdate: "popperOnUpdate"
      },
      exportAs: ["metPopper"]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let PopperModule = /*#__PURE__*/(() => {
  class PopperModule {
    static {
      this.ɵfac = function PopperModule_Factory(t) {
        return new (t || PopperModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: PopperModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [CommonModule]
      });
    }
  }
  return PopperModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { PopperContentComponent, PopperDirective, PopperModule, PopperPlacements, PopperTriggers };
